import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/system';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';

// Styled Section
const Section = styled(Box)(({ theme }) => ({
  backgroundColor: '#f5f7fa',
  padding: theme.spacing(10, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 2),
  },
}));

const ImageWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  transition: 'transform 0.6s ease',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const Image = styled('img')({
  width: '100%',
  height: 'auto',
  display: 'block',
});

const AnimatedBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'animate',
})(({ animate }) => ({
  opacity: 0,
  transform: 'translateY(40px)',
  transition: 'opacity 1s ease, transform 1s ease',
  ...(animate && {
    opacity: 1,
    transform: 'translateY(0)',
  }),
}));

export default function AboutUs() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
  const textAlign = direction === 'rtl' ? 'right' : 'left';

  const isRTL = i18n.language === 'ar';

  const fontFamily = isRTL ? "'Tajawal', sans-serif" : 'inherit';

  const { ref: textRef, inView: textInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });
  const { ref: imageRef, inView: imageInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const pdfUrl = 'LUL.pdf';
  const openPdf = () => window.open(pdfUrl, '_blank');

  return (
    <Section dir={direction}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* Left: Image */}
          <Grid item xs={12} md={6}>
            <AnimatedBox ref={imageRef} animate={imageInView}>
              <ImageWrapper>
                <Image src="/image/sd.png" alt={t('about.alt')} />
              </ImageWrapper>
            </AnimatedBox>
          </Grid>

          {/* Right: Text Content */}
          <Grid item xs={12} md={6}>
            <AnimatedBox ref={textRef} animate={textInView}>
           <Typography
  fontFamily={fontFamily}
  variant="h5"
  fontWeight="bold"
  gutterBottom
  sx={{ color: '#0d1b2a' }}
>
  {t('about.title')}
</Typography>


            <Typography
  fontFamily={fontFamily}
  variant="body1"
  color="text.secondary"
  sx={{
    fontSize: { xs: '1rem', md: '1.15rem' },
    lineHeight: 1.8,
    mb: 3,
    textAlign: 'justify',  // <-- here
  }}
>
  {t('about.intro')}
</Typography>


            {[t('about.detail1'), t('about.detail2'), t('about.detail3')].map((item, index) => (
  <Typography
    fontFamily={fontFamily}
    key={index}
    variant="body1"
    color="text.secondary"
    sx={{ mb: 2, textAlign: 'justify', lineHeight: 1.6 }}
  >
    {item}
  </Typography>
))}

             

              <Box sx={{ mt: 4, display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                <Button
                  variant="contained"
                  onClick={openPdf}
                  size="large"
                  sx={{
                      fontFamily,
                    backgroundColor: '#0056b3',
                    '&:hover': {
                      backgroundColor: '#003d80',
                    },
                    px: 5,
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: '8px',
                    textTransform: 'none',
                  }}
                >
                  {t('about.readMore')}
                </Button>
              </Box>
            </AnimatedBox>
          </Grid>
        </Grid>
      </Container>
    </Section>
  );
}
